import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as L from 'leaflet';
import { GeoJsonObject, Geometry, Position } from 'geojson';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import {
  ContactAutocompleteResult,
  ContactAutocompleteService
} from '../../services/contacts/contact-autocomplete.service';
import {
  ZoneExpertCleanupOptions,
  ZoneExpertPreviewData,
  ZoneExpertReferenceBoundary,
  ZoneExpertReferenceBoundaryType,
  ZoneExpertSaveRequest,
  ZoneExpertZone,
  ZoneExpertZoneType,
  ZoneMakerExpertApiService
} from './zone-maker-expert-api.service';

type ExpertMode = 'pan' | 'draw' | 'edit';

interface SelectedVertex {
  ringIndex: number;
  vertexIndex: number;
}

interface ZoneMakerExpertLastContact {
  zoneType: ZoneExpertZoneType;
  contactUuid: string;
  contactLabel: string;
  country: string;
}

interface RingBounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

@Component({
  selector: 'app-zone-maker-expert-mode',
  templateUrl: './zone-maker-expert-mode.component.html',
  styleUrls: ['./zone-maker-expert-mode.component.scss']
})
export class ZoneMakerExpertModeComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapBox', { static: false }) mapBox?: ElementRef<HTMLDivElement>;

  readonly uuidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  readonly referenceBoundaryTypes: Array<{ type: ZoneExpertReferenceBoundaryType; label: string; icon: string; country: 'USA' | 'CAN' }> = [
    { type: 'place', label: 'US Cities', icon: 'fa-city', country: 'USA' },
    { type: 'municipality', label: 'Canada Cities', icon: 'fa-map-location-dot', country: 'CAN' }
  ];
  private readonly lastContactStorageKey = 'zoneMaker:lastContact';

  zoneType: ZoneExpertZoneType = 'customer';
  country = 'USA';
  active = true;
  zoneName = '';
  zoneId = '';
  description = '';
  editingZoneUuid = '';

  contactSearchTerm = '';
  selectedContactUuid = '';
  selectedContact: ContactAutocompleteResult | null = null;
  contactOptions: ContactAutocompleteResult[] = [];
  contactLoading = false;
  contactError = '';

  zones: ZoneExpertZone[] = [];
  zonesLoading = false;

  referenceBoundaryType: ZoneExpertReferenceBoundaryType = 'place';
  referenceLayerVisible = false;
  referenceSearchTerm = '';
  referenceBoundaries: ZoneExpertReferenceBoundary[] = [];
  selectedReferenceBoundaryUuid = '';
  referenceViewportBoundaries: ZoneExpertReferenceBoundary[] = [];
  referenceLoading = false;
  referenceViewportLoading = false;
  selectedReferenceBoundary: ZoneExpertReferenceBoundary | null = null;

  mode: ExpertMode = 'pan';
  rings: L.LatLng[][] = [];
  activeRingIndex = 0;
  selectedVertex: SelectedVertex | null = null;

  minHoleSquareMiles = 0.1;
  simplifyToleranceDegrees = 0;
  previewLoading = false;
  previewMode = false;
  saving = false;
  validating = false;
  previewData: ZoneExpertPreviewData | null = null;
  previewWarnings: string[] = [];
  validationErrors: string[] = [];
  overlapZones: ZoneExpertZone[] = [];

  private map?: L.Map;
  private editLayer?: L.LayerGroup;
  private vertexLayer?: L.LayerGroup;
  private midpointLayer?: L.LayerGroup;
  private previewLayer?: L.GeoJSON;
  private referenceViewportLayer?: L.GeoJSON;
  private referenceLayer?: L.GeoJSON;
  private zonesLayer?: L.GeoJSON;
  private referenceViewportTimer: number | null = null;
  private referenceViewportRequestId = 0;
  private readonly subscriptions = new Subscription();
  private readonly contactSearch$ = new Subject<string>();

  constructor(
    private readonly api: ZoneMakerExpertApiService,
    private readonly contactAutocomplete: ContactAutocompleteService,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.restoreLastContact();

    this.subscriptions.add(
      this.contactSearch$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(query => {
          this.contactError = '';
          this.contactLoading = query.trim().length >= 3;
        }),
        switchMap(query => {
          const cleanQuery = query.trim();
          if (cleanQuery.length < 3) {
            this.contactLoading = false;
            return of([]);
          }

          return this.contactAutocomplete.search(this.contactAutocompleteType, cleanQuery).pipe(
            catchError(() => {
              this.contactError = 'Contact search failed';
              return of([]);
            })
          );
        })
      ).subscribe(options => {
        this.contactOptions = options;
        this.contactLoading = false;
      })
    );
  }

  ngAfterViewInit(): void {
    if (!this.mapBox) {
      return;
    }

    this.map = L.map(this.mapBox.nativeElement, {
      center: [39.5, -98.35],
      zoom: 5,
      preferCanvas: true
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(this.map);

    this.zonesLayer = L.geoJSON(undefined, {
      interactive: false,
      style: {
        color: '#64748b',
        weight: 2,
        opacity: 0.72,
        fillColor: '#64748b',
        fillOpacity: 0.08
      }
    }).addTo(this.map);

    this.referenceViewportLayer = L.geoJSON(undefined, {
      interactive: true
    }).addTo(this.map);

    this.referenceLayer = L.geoJSON(undefined, {
      interactive: false,
      style: {
        color: '#f59e0b',
        weight: 3,
        opacity: 0.92,
        fillColor: '#fbbf24',
        fillOpacity: 0.13,
        dashArray: '8 5'
      }
    }).addTo(this.map);

    this.editLayer = L.layerGroup().addTo(this.map);
    this.vertexLayer = L.layerGroup().addTo(this.map);
    this.midpointLayer = L.layerGroup().addTo(this.map);

    this.map.on('click', event => this.onMapClick(event.latlng));

    window.setTimeout(() => {
      this.map?.invalidateSize();
      if (this.selectedContactUuid) {
        this.loadZones();
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.referenceViewportTimer !== null) {
      window.clearTimeout(this.referenceViewportTimer);
    }
    this.map?.remove();
  }

  get contactAutocompleteType(): string {
    return this.zoneType === 'carrier' ? 'carrier_contact_list' : 'customer_contact_list';
  }

  get selectedContactLabel(): string {
    return this.selectedContact?.label || this.selectedContactUuid || 'No contact selected';
  }

  get canPreview(): boolean {
    return this.boundaryGeoJson() !== null && !this.previewLoading && !this.previewMode;
  }

  get canSave(): boolean {
    return this.selectedContactUuid !== ''
      && this.zoneName.trim() !== ''
      && this.boundaryGeoJson() !== null
      && !this.saving;
  }

  get vertexCount(): number {
    return this.rings.reduce((total, ring) => total + ring.length, 0);
  }

  get polygonCount(): number {
    return this.validRings().length;
  }

  get overlapZoneNames(): string {
    const names = this.overlapZones.map(zone => this.zoneDisplayName(zone));
    if (names.length <= 3) {
      return names.join(', ');
    }

    return names.slice(0, 3).join(', ') + ' +' + (names.length - 3) + ' more';
  }

  get selectedReferenceLayerCountry(): 'USA' | 'CAN' {
    return this.referenceBoundaryTypes.find(option => option.type === this.referenceBoundaryType)?.country || 'USA';
  }

  setZoneType(zoneType: ZoneExpertZoneType): void {
    if (this.zoneType === zoneType) {
      return;
    }

    this.zoneType = zoneType;
    this.clearContact();
    this.clearZoneDraft(false);
    this.removeLastContact();
  }

  onContactSearchChange(value: string): void {
    this.contactSearchTerm = value;
    this.contactSearch$.next(value);
  }

  selectContact(option: ContactAutocompleteResult): void {
    this.selectedContact = option;
    this.selectedContactUuid = option.value;
    this.contactSearchTerm = option.label;
    this.contactOptions = [];
    this.persistLastContact();
    this.loadZones();
  }

  applyContactUuid(): void {
    const uuid = this.contactSearchTerm.trim().toLowerCase();
    if (!this.uuidPattern.test(uuid)) {
      this.toastr.error('Enter a valid contact UUID');
      return;
    }

    this.selectedContact = null;
    this.selectedContactUuid = uuid;
    this.contactOptions = [];
    this.persistLastContact();
    this.loadZones();
  }



  setReferenceBoundaryType(type: ZoneExpertReferenceBoundaryType): void {
    if (this.referenceBoundaryType === type) {
      return;
    }

    this.referenceBoundaryType = type;
    this.referenceBoundaries = [];
    this.selectedReferenceBoundaryUuid = '';
    this.selectedReferenceBoundary = null;
    this.referenceLayer?.clearLayers();
    this.referenceViewportLayer?.clearLayers();
  }

  toggleReferenceLayerVisible(): void {
    this.referenceLayerVisible = !this.referenceLayerVisible;
    if (this.referenceLayerVisible) {
      this.loadReferenceViewportBoundaries();
      return;
    }

    this.referenceViewportBoundaries = [];
    this.referenceViewportLayer?.clearLayers();
  }

  refreshReferenceLayer(): void {
    this.loadReferenceViewportBoundaries();
  }

  searchReferenceBoundaries(): void {
    const query = this.referenceSearchTerm.trim();
    if (query.length < 2) {
      this.toastr.info('Enter at least 2 characters');
      return;
    }

    this.referenceLoading = true;
    this.selectedReferenceBoundaryUuid = '';
    this.selectedReferenceBoundary = null;
    this.referenceLayer?.clearLayers();
    this.api.searchReferenceBoundaries(query, this.selectedReferenceLayerCountry, this.referenceBoundaryType, 25).subscribe({
      next: response => {
        if (!response.ok) {
          this.toastr.error(response.message || 'Reference boundaries failed to load');
          return;
        }

        this.referenceBoundaries = response.data || [];
        if (response.meta?.['table_ready'] === false) {
          this.toastr.info('Reference boundary table is not ready yet');
        }
      },
      error: error => this.toastr.error(this.apiErrorMessage(error, 'Reference boundaries failed to load')),
      complete: () => {
        this.referenceLoading = false;
      }
    });
  }

  selectReferenceBoundaryUuid(boundaryUuid: string): void {
    this.selectedReferenceBoundaryUuid = boundaryUuid;
    const boundary = this.referenceBoundaries.find(item => item.reference_boundary_uuid === boundaryUuid);
    if (!boundary) {
      this.selectedReferenceBoundary = null;
      this.referenceLayer?.clearLayers();
      return;
    }

    this.loadReferenceBoundary(boundary);
  }

  loadReferenceBoundary(boundary: ZoneExpertReferenceBoundary): void {
    if (boundary.boundary_geojson) {
      this.showReferenceBoundary(boundary, true);
      return;
    }

    this.referenceLoading = true;
    this.api.getReferenceBoundary(boundary.reference_boundary_uuid).subscribe({
      next: response => {
        if (!response.ok) {
          this.toastr.error(response.message || 'Reference boundary failed to load');
          return;
        }

        this.rememberReferenceBoundary(response.data);
        this.showReferenceBoundary(response.data, true);
      },
      error: error => this.toastr.error(this.apiErrorMessage(error, 'Reference boundary failed to load')),
      complete: () => {
        this.referenceLoading = false;
      }
    });
  }

  useReferenceBoundary(boundary: ZoneExpertReferenceBoundary | null = this.selectedReferenceBoundary): void {
    if (!boundary) {
      return;
    }

    if (boundary.boundary_geojson) {
      this.applyReferenceBoundary(boundary);
      return;
    }

    this.referenceLoading = true;
    this.api.getReferenceBoundary(boundary.reference_boundary_uuid).subscribe({
      next: response => {
        if (!response.ok) {
          this.toastr.error(response.message || 'Reference boundary failed to load');
          return;
        }

        this.rememberReferenceBoundary(response.data);
        this.applyReferenceBoundary(response.data);
      },
      error: error => this.toastr.error(this.apiErrorMessage(error, 'Reference boundary failed to load')),
      complete: () => {
        this.referenceLoading = false;
      }
    });
  }

  clearReferenceBoundary(): void {
    this.selectedReferenceBoundaryUuid = '';
    this.selectedReferenceBoundary = null;
    this.referenceLayer?.clearLayers();
  }

  clearContact(): void {
    this.contactSearchTerm = '';
    this.selectedContactUuid = '';
    this.selectedContact = null;
    this.contactOptions = [];
    this.zones = [];
    this.overlapZones = [];
    this.zonesLayer?.clearLayers();
    this.removeLastContact();
  }

  loadZones(): void {
    if (!this.selectedContactUuid) {
      this.toastr.error('Select a contact first');
      return;
    }

    this.zonesLoading = true;
    this.api.listZones(this.zoneType, this.selectedContactUuid, true).subscribe({
      next: response => {
        if (!response.ok) {
          this.toastr.error(response.message || 'Zones failed to load');
          return;
        }
        this.zones = response.data || [];
        this.drawBackgroundZones();
      },
      error: error => this.toastr.error(this.apiErrorMessage(error, 'Zones failed to load')),
      complete: () => {
        this.zonesLoading = false;
      }
    });
  }

  loadZone(zone: ZoneExpertZone): void {
    this.api.getZone(this.zoneType, zone.zone_uuid, this.selectedContactUuid).subscribe({
      next: response => {
        if (!response.ok) {
          this.toastr.error(response.message || 'Zone failed to load');
          return;
        }

        const detail = response.data;
        this.editingZoneUuid = detail.zone_uuid;
        this.zoneName = detail.name || detail.zone_name || '';
        this.zoneId = detail.zone_id || '';
        this.country = detail.country || 'USA';
        this.active = detail.active !== false;
        this.description = detail.description || '';
        this.previewData = null;
        this.previewWarnings = [];
        this.validationErrors = [];
        this.previewLayer?.remove();
        this.previewLayer = undefined;
        this.rings = this.geometryToRings(detail.boundary_geojson || null);
        this.activeRingIndex = 0;
        this.selectedVertex = null;
        this.mode = 'edit';
        this.drawBackgroundZones();
        this.renderEditableGeometry(true);
      },
      error: error => this.toastr.error(this.apiErrorMessage(error, 'Zone failed to load'))
    });
  }

  setMode(mode: ExpertMode): void {
    if (this.previewMode) {
      this.clearPreview();
    }

    this.mode = mode;
    if (mode === 'draw' && this.rings.length === 0) {
      this.startNewPolygon();
    }
    this.renderEditableGeometry(false);
  }

  startNewZone(): void {
    this.clearZoneDraft(true);
    this.mode = 'draw';
    this.startNewPolygon();
  }

  startNewPolygon(): void {
    this.rings.push([]);
    this.activeRingIndex = this.rings.length - 1;
    this.selectedVertex = null;
    this.mode = 'draw';
    this.clearPreview();
    this.renderEditableGeometry(false);
  }

  finishPolygon(): void {
    const ring = this.rings[this.activeRingIndex] || [];
    if (ring.length < 3) {
      this.toastr.error('Add at least 3 points');
      return;
    }

    this.mode = 'edit';
    this.selectedVertex = null;
    this.renderEditableGeometry(false);
  }

  undoPoint(): void {
    const ring = this.rings[this.activeRingIndex];
    if (!ring || ring.length === 0) {
      return;
    }

    ring.pop();
    this.clearPreview();
    this.renderEditableGeometry(false);
  }

  removeSelectedVertex(): void {
    if (!this.selectedVertex) {
      return;
    }

    const ring = this.rings[this.selectedVertex.ringIndex];
    if (!ring) {
      this.selectedVertex = null;
      return;
    }

    if (ring.length <= 3) {
      this.toastr.info('A polygon needs at least 3 points');
      return;
    }

    ring.splice(this.selectedVertex.vertexIndex, 1);
    this.selectedVertex = null;
    this.clearPreview();
    this.renderEditableGeometry(false);
  }

  clearGeometry(): void {
    this.rings = [];
    this.activeRingIndex = 0;
    this.selectedVertex = null;
    this.clearPreview();
    this.renderEditableGeometry(false);
  }

  onMinHoleSquareMilesChange(value: string | number): void {
    const parsed = Number(value);
    this.minHoleSquareMiles = Number.isFinite(parsed) ? Math.min(10000, Math.max(0, parsed)) : 0;
    this.clearPreview();
  }

  onSimplifyToleranceChange(value: string | number): void {
    const parsed = Number(value);
    this.simplifyToleranceDegrees = Number.isFinite(parsed) ? Math.min(0.1, Math.max(0, parsed)) : 0;
    this.clearPreview();
  }

  previewBoundary(): void {
    const request = this.previewRequest();
    if (!request) {
      return;
    }

    this.previewLoading = true;
    this.validationErrors = [];
    this.api.preview(request).subscribe({
      next: response => {
        if (!response.ok) {
          this.validationErrors = response.errors || [response.message || 'Preview failed'];
          this.toastr.error(response.message || 'Preview failed');
          return;
        }

        this.previewData = response.data;
        this.previewWarnings = response.warnings || [];
        this.enterPreviewMode();
        this.drawPreview(response.data.boundary_geojson);
      },
      error: error => this.toastr.error(this.apiErrorMessage(error, 'Preview failed')),
      complete: () => {
        this.previewLoading = false;
      }
    });
  }

  validateBoundary(): void {
    const request = this.previewRequest();
    if (!request) {
      return;
    }

    this.validating = true;
    this.validationErrors = [];
    this.api.validate(request).subscribe({
      next: response => {
        this.previewWarnings = response.warnings || [];
        if (!response.ok || response.valid === false) {
          this.validationErrors = response.errors || [response.message || 'Geometry is not valid'];
          this.toastr.error('Geometry needs attention');
          return;
        }

        this.toastr.success('Geometry validated');
      },
      error: error => this.toastr.error(this.apiErrorMessage(error, 'Validation failed')),
      complete: () => {
        this.validating = false;
      }
    });
  }

  usePreviewGeometry(): void {
    if (!this.previewData) {
      return;
    }

    this.rings = this.geometryToRings(this.previewData.boundary_geojson);
    this.selectedVertex = null;
    this.mode = 'edit';
    this.clearPreview();
    this.renderEditableGeometry(true);
  }

  exitPreviewMode(): void {
    this.clearPreview();
    this.renderEditableGeometry(false);
  }

  saveZone(): void {
    const request = this.saveRequest();
    if (!request) {
      return;
    }

    this.saving = true;
    const wasEditing = this.editingZoneUuid !== '';
    const save$ = wasEditing
      ? this.api.update(this.editingZoneUuid, request)
      : this.api.create(request);

    save$.subscribe({
      next: response => {
        if (!response.ok) {
          this.validationErrors = response.errors || [response.message || 'Save failed'];
          this.toastr.error(response.message || 'Save failed');
          return;
        }

        const saved = response.data;
        this.editingZoneUuid = saved.zone_uuid || this.editingZoneUuid;
        this.previewWarnings = response.warnings || [];
        this.toastr.success(wasEditing ? 'Zone updated' : 'Zone created');
        this.loadZones();
      },
      error: error => this.toastr.error(this.apiErrorMessage(error, 'Save failed')),
      complete: () => {
        this.saving = false;
      }
    });
  }

  private restoreLastContact(): void {
    const snapshot = this.readLastContact();
    if (!snapshot) {
      return;
    }

    this.zoneType = snapshot.zoneType;
    this.country = snapshot.country;
    this.selectedContactUuid = snapshot.contactUuid;
    this.contactSearchTerm = snapshot.contactLabel || snapshot.contactUuid;
    this.selectedContact = snapshot.contactLabel
      ? { value: snapshot.contactUuid, label: snapshot.contactLabel }
      : null;
    this.contactOptions = [];
    this.contactError = '';
  }

  private persistLastContact(): void {
    if (!this.selectedContactUuid || !this.canUseBrowserStorage()) {
      return;
    }

    const snapshot: ZoneMakerExpertLastContact = {
      zoneType: this.zoneType,
      contactUuid: this.selectedContactUuid,
      contactLabel: this.selectedContact?.label || this.contactSearchTerm.trim(),
      country: this.country.trim().toUpperCase() || 'USA'
    };

    try {
      window.localStorage.setItem(this.lastContactStorageKey, JSON.stringify(snapshot));
    } catch {
      // Local storage can be unavailable in restricted browser contexts.
    }
  }

  private readLastContact(): ZoneMakerExpertLastContact | null {
    if (!this.canUseBrowserStorage()) {
      return null;
    }

    try {
      const raw = window.localStorage.getItem(this.lastContactStorageKey);
      if (!raw) {
        return null;
      }

      const value = JSON.parse(raw) as Partial<ZoneMakerExpertLastContact>;
      const zoneType: ZoneExpertZoneType = value.zoneType === 'carrier' ? 'carrier' : 'customer';
      const contactUuid = (value.contactUuid || '').toLowerCase();
      if (!this.uuidPattern.test(contactUuid)) {
        window.localStorage.removeItem(this.lastContactStorageKey);
        return null;
      }

      return {
        zoneType,
        contactUuid,
        contactLabel: (value.contactLabel || '').trim(),
        country: this.normalizeCountry(value.country)
      };
    } catch {
      window.localStorage.removeItem(this.lastContactStorageKey);
      return null;
    }
  }

  private removeLastContact(): void {
    if (!this.canUseBrowserStorage()) {
      return;
    }

    try {
      window.localStorage.removeItem(this.lastContactStorageKey);
    } catch {
      // Ignore storage cleanup failures.
    }
  }

  private canUseBrowserStorage(): boolean {
    try {
      return typeof window !== 'undefined' && !!window.localStorage;
    } catch {
      return false;
    }
  }

  private normalizeCountry(country: string | null | undefined): string {
    const clean = (country || '').trim().toUpperCase();
    if (clean === 'CAN' || clean === 'CA') {
      return 'CAN';
    }

    if (clean === 'USA' || clean === 'US') {
      return 'USA';
    }

    return 'USA';
  }



  private scheduleReferenceViewportLoad(): void {
    if (this.referenceViewportTimer !== null) {
      window.clearTimeout(this.referenceViewportTimer);
    }

    this.referenceViewportTimer = window.setTimeout(() => {
      this.referenceViewportTimer = null;
      this.loadReferenceViewportBoundaries();
    }, 250);
  }

  private loadReferenceViewportBoundaries(): void {
    if (!this.map || !this.referenceLayerVisible) {
      this.referenceViewportLayer?.clearLayers();
      return;
    }

    const bounds = this.map.getBounds();
    const requestId = ++this.referenceViewportRequestId;
    this.referenceViewportLoading = true;

    this.api.bboxReferenceBoundaries({
      minLat: bounds.getSouth(),
      minLng: bounds.getWest(),
      maxLat: bounds.getNorth(),
      maxLng: bounds.getEast()
    }, this.selectedReferenceLayerCountry, this.referenceBoundaryType, 150).subscribe({
      next: response => {
        if (requestId !== this.referenceViewportRequestId) {
          return;
        }

        if (!response.ok || response.meta?.['table_ready'] === false) {
          this.referenceViewportBoundaries = [];
          this.referenceViewportLayer?.clearLayers();
          return;
        }

        this.referenceViewportBoundaries = response.data || [];
        this.drawReferenceViewportBoundaries();
      },
      error: () => {
        if (requestId === this.referenceViewportRequestId) {
          this.referenceViewportBoundaries = [];
          this.referenceViewportLayer?.clearLayers();
        }
      },
      complete: () => {
        if (requestId === this.referenceViewportRequestId) {
          this.referenceViewportLoading = false;
        }
      }
    });
  }

  private drawReferenceViewportBoundaries(): void {
    this.referenceViewportLayer?.clearLayers();
    if (!this.referenceViewportLayer || !this.referenceLayerVisible) {
      return;
    }

    this.referenceViewportBoundaries.forEach(boundary => {
      if (!boundary.boundary_geojson) {
        return;
      }

      const selected = boundary.reference_boundary_uuid === this.selectedReferenceBoundary?.reference_boundary_uuid;
      const layer = L.geoJSON(boundary.boundary_geojson as GeoJsonObject, {
        interactive: true,
        style: {
          color: selected ? '#f59e0b' : '#fbbf24',
          weight: selected ? 4 : 2,
          opacity: selected ? 0.95 : 0.62,
          fillColor: '#fbbf24',
          fillOpacity: selected ? 0.13 : 0.045,
          dashArray: selected ? '8 4' : '5 6'
        }
      }).addTo(this.referenceViewportLayer as L.GeoJSON);

      layer.on('click', event => {
        L.DomEvent.stopPropagation(event);
        this.showReferenceBoundary(boundary, false);
        this.drawReferenceViewportBoundaries();
      });
    });

    this.zonesLayer?.bringToBack();
  }

  private rememberReferenceBoundary(boundary: ZoneExpertReferenceBoundary): void {
    const index = this.referenceBoundaries.findIndex(item => item.reference_boundary_uuid === boundary.reference_boundary_uuid);
    if (index >= 0) {
      this.referenceBoundaries[index] = boundary;
    }
  }

  private showReferenceBoundary(boundary: ZoneExpertReferenceBoundary, fitBounds: boolean): void {
    if (!boundary.boundary_geojson) {
      return;
    }

    this.rememberReferenceBoundary(boundary);
    this.selectedReferenceBoundaryUuid = boundary.reference_boundary_uuid;
    this.selectedReferenceBoundary = boundary;
    this.drawReferenceBoundary(boundary.boundary_geojson, fitBounds);
  }

  private applyReferenceBoundary(boundary: ZoneExpertReferenceBoundary): void {
    if (!boundary.boundary_geojson) {
      this.toastr.error('Reference boundary has no geometry');
      return;
    }

    const rings = this.geometryToRings(boundary.boundary_geojson);
    if (rings.length === 0) {
      this.toastr.error('Reference boundary geometry is empty');
      return;
    }

    this.rings = rings;
    this.activeRingIndex = 0;
    this.selectedVertex = null;
    this.mode = 'edit';
    this.country = this.normalizeCountry(boundary.country_code);
    if (this.zoneName.trim() === '') {
      this.zoneName = boundary.name;
    }
    if (this.zoneId.trim() === '') {
      this.zoneId = boundary.source_id;
    }

    this.clearPreview();
    this.showReferenceBoundary(boundary, false);
    this.renderEditableGeometry(true);
  }

  private drawReferenceBoundary(geometry: Geometry, fitBounds: boolean): void {
    this.referenceLayer?.clearLayers();
    if (!this.referenceLayer) {
      return;
    }

    const layer = L.geoJSON(geometry as GeoJsonObject, {
      interactive: false,
      style: {
        color: '#f59e0b',
        weight: 3,
        opacity: 0.92,
        fillColor: '#fbbf24',
        fillOpacity: 0.13,
        dashArray: '8 5'
      }
    }).addTo(this.referenceLayer);

    this.referenceViewportLayer?.bringToBack();
    this.zonesLayer?.bringToBack();
    this.referenceLayer.bringToFront();

    if (fitBounds) {
      const bounds = layer.getBounds();
      if (bounds.isValid()) {
        this.requireMap().fitBounds(bounds.pad(0.2));
      }
    }
  }

  private clearZoneDraft(clearIdentity: boolean): void {
    if (clearIdentity) {
      this.editingZoneUuid = '';
      this.zoneName = '';
      this.zoneId = '';
      this.description = '';
      this.active = true;
      this.country = 'USA';
    }

    this.rings = [];
    this.activeRingIndex = 0;
    this.selectedVertex = null;
    this.previewMode = false;
    this.previewData = null;
    this.previewWarnings = [];
    this.validationErrors = [];
    this.previewLayer?.remove();
    this.previewLayer = undefined;
    this.drawBackgroundZones();
    this.renderEditableGeometry(false);
  }

  private drawBackgroundZones(): void {
    this.zonesLayer?.clearLayers();
    if (!this.zonesLayer) {
      this.refreshLocalOverlapWarning();
      return;
    }

    this.zones
      .filter(zone => zone.zone_uuid !== this.editingZoneUuid)
      .forEach(zone => {
        const geometry = zone.boundary_geojson;
        if (!geometry) {
          return;
        }

        L.geoJSON(geometry as GeoJsonObject, {
          interactive: false,
          style: {
            color: '#64748b',
            weight: 2,
            opacity: 0.78,
            fillColor: '#94a3b8',
            fillOpacity: 0.1,
            dashArray: '6 4'
          }
        }).addTo(this.zonesLayer as L.GeoJSON);
      });

    this.zonesLayer.bringToBack();
    this.refreshLocalOverlapWarning();
  }

  private onMapClick(latlng: L.LatLng): void {
    if (this.mode !== 'draw') {
      return;
    }

    const ring = this.ensureActiveRing();
    ring.push(latlng);
    this.clearPreview();
    this.renderEditableGeometry(false);
  }

  private ensureActiveRing(): L.LatLng[] {
    if (!this.rings[this.activeRingIndex]) {
      this.rings.push([]);
      this.activeRingIndex = this.rings.length - 1;
    }

    return this.rings[this.activeRingIndex];
  }

  private renderEditableGeometry(fitBounds: boolean): void {
    this.editLayer?.clearLayers();
    this.vertexLayer?.clearLayers();
    this.midpointLayer?.clearLayers();
    this.refreshLocalOverlapWarning();

    if (this.previewMode) {
      return;
    }

    const boundsLayers: L.Layer[] = [];
    const hasOverlap = this.overlapZones.length > 0;
    this.rings.forEach((ring, ringIndex) => {
      const isActiveRing = ringIndex === this.activeRingIndex;
      if (ring.length >= 3) {
        const polygon = L.polygon(ring, {
          interactive: false,
          color: isActiveRing && hasOverlap ? '#f43f5e' : (isActiveRing ? '#14b8a6' : '#38bdf8'),
          weight: isActiveRing && hasOverlap ? 5 : 3,
          opacity: 0.95,
          fillColor: isActiveRing && hasOverlap ? '#fb7185' : (isActiveRing ? '#5eead4' : '#93c5fd'),
          fillOpacity: isActiveRing && hasOverlap ? 0.32 : 0.2,
          dashArray: isActiveRing && hasOverlap ? '8 5' : undefined
        }).addTo(this.requireLayer(this.editLayer));
        boundsLayers.push(polygon);
      } else if (ring.length >= 2) {
        const line = L.polyline(ring, {
          interactive: false,
          color: hasOverlap ? '#f43f5e' : '#14b8a6',
          weight: hasOverlap ? 5 : 3,
          opacity: 0.9,
          dashArray: '6 4'
        }).addTo(this.requireLayer(this.editLayer));
        boundsLayers.push(line);
      }

      ring.forEach((point, vertexIndex) => {
        const selected = this.selectedVertex?.ringIndex === ringIndex && this.selectedVertex.vertexIndex === vertexIndex;
        const marker = L.marker(point, {
          draggable: true,
          icon: this.vertexIcon(selected),
          keyboard: false
        }).addTo(this.requireLayer(this.vertexLayer));

        marker.on('click', event => {
          L.DomEvent.stopPropagation(event);
          this.selectedVertex = { ringIndex, vertexIndex };
          this.activeRingIndex = ringIndex;
          this.renderEditableGeometry(false);
        });

        marker.on('dragend', () => {
          this.rings[ringIndex][vertexIndex] = marker.getLatLng();
          this.clearPreview();
          this.renderEditableGeometry(false);
        });
      });

      if (this.mode === 'edit') {
        this.drawMidpointMarkers(ring, ringIndex);
      }
    });

    if (fitBounds && boundsLayers.length > 0) {
      const group = L.featureGroup(boundsLayers);
      this.requireMap().fitBounds(group.getBounds().pad(0.2));
    }
  }

  private drawMidpointMarkers(ring: L.LatLng[], ringIndex: number): void {
    if (ring.length < 3) {
      return;
    }

    for (let vertexIndex = 0; vertexIndex < ring.length; vertexIndex++) {
      const nextIndex = (vertexIndex + 1) % ring.length;
      const midpoint = L.latLng(
        (ring[vertexIndex].lat + ring[nextIndex].lat) / 2,
        (ring[vertexIndex].lng + ring[nextIndex].lng) / 2
      );
      const marker = L.marker(midpoint, {
        icon: this.midpointIcon(),
        keyboard: false
      }).addTo(this.requireLayer(this.midpointLayer));

      marker.on('click', event => {
        L.DomEvent.stopPropagation(event);
        this.rings[ringIndex].splice(nextIndex, 0, midpoint);
        this.selectedVertex = { ringIndex, vertexIndex: nextIndex };
        this.activeRingIndex = ringIndex;
        this.clearPreview();
        this.renderEditableGeometry(false);
      });
    }
  }

  private vertexIcon(selected: boolean): L.DivIcon {
    return L.divIcon({
      className: selected ? 'zm-expert-vertex is-selected' : 'zm-expert-vertex',
      iconSize: [16, 16],
      iconAnchor: [8, 8]
    });
  }

  private midpointIcon(): L.DivIcon {
    return L.divIcon({
      className: 'zm-expert-midpoint',
      iconSize: [12, 12],
      iconAnchor: [6, 6]
    });
  }

  private enterPreviewMode(): void {
    this.previewMode = true;
    this.selectedVertex = null;
    this.editLayer?.clearLayers();
    this.vertexLayer?.clearLayers();
    this.midpointLayer?.clearLayers();
  }

  private drawPreview(geometry: Geometry): void {
    this.previewLayer?.remove();
    this.previewLayer = L.geoJSON(geometry as GeoJsonObject, {
      interactive: false,
      style: {
        color: '#f59e0b',
        weight: 4,
        opacity: 0.98,
        fillColor: '#fbbf24',
        fillOpacity: 0.18,
        dashArray: '8 5'
      }
    }).addTo(this.requireMap());
    this.previewLayer.bringToFront();
  }

  private clearPreview(clearData = true): void {
    this.previewMode = false;
    if (clearData) {
      this.previewData = null;
      this.previewWarnings = [];
      this.validationErrors = [];
    }
    this.previewLayer?.remove();
    this.previewLayer = undefined;
  }

  private previewRequest(): import('./zone-maker-expert-api.service').ZoneExpertPreviewRequest | null {
    const boundary = this.boundaryGeoJson();
    if (!boundary) {
      this.toastr.error('Draw a polygon first');
      return null;
    }

    const request: import('./zone-maker-expert-api.service').ZoneExpertPreviewRequest = {
      zone_type: this.zoneType,
      contact_uuid: this.selectedContactUuid || undefined,
      zone_uuid: this.editingZoneUuid || undefined,
      country: this.country.trim() || 'USA',
      source_type: 'manual',
      boundary_geojson: boundary,
      cleanup: this.cleanupOptions()
    };

    return request;
  }

  private saveRequest(): ZoneExpertSaveRequest | null {
    const preview = this.previewRequest();
    if (!preview) {
      return null;
    }

    if (!this.selectedContactUuid) {
      this.toastr.error('Select a contact first');
      return null;
    }

    if (this.zoneName.trim() === '') {
      this.toastr.error('Name the zone first');
      return null;
    }

    return {
      ...preview,
      contact_uuid: this.selectedContactUuid,
      name: this.zoneName.trim(),
      zone_id: this.zoneId.trim() || null,
      description: this.zoneType === 'customer' ? (this.description.trim() || null) : null,
      active: this.active,
      sync_postal_codes: false
    };
  }

  private cleanupOptions(): ZoneExpertCleanupOptions {
    return {
      min_hole_square_miles: this.minHoleSquareMiles,
      simplify_tolerance_degrees: this.simplifyToleranceDegrees
    };
  }

  private boundaryGeoJson(): Geometry | null {
    const rings = this.validRings();
    if (rings.length === 0) {
      return null;
    }

    if (rings.length === 1) {
      return {
        type: 'Polygon',
        coordinates: [this.closedCoordinates(rings[0])]
      };
    }

    return {
      type: 'MultiPolygon',
      coordinates: rings.map(ring => [this.closedCoordinates(ring)])
    };
  }

  private validRings(): L.LatLng[][] {
    return this.rings
      .map(ring => ring.filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng)))
      .filter(ring => ring.length >= 3);
  }

  private closedCoordinates(ring: L.LatLng[]): Position[] {
    const coordinates = ring.map(point => [point.lng, point.lat] as Position);
    const first = coordinates[0];
    const last = coordinates[coordinates.length - 1];
    if (!this.samePosition(first, last)) {
      coordinates.push([first[0], first[1]] as Position);
    }

    return coordinates;
  }

  private samePosition(a: Position, b: Position): boolean {
    return Number(a[0]) === Number(b[0]) && Number(a[1]) === Number(b[1]);
  }

  private refreshLocalOverlapWarning(): void {
    const activeRings = this.validRings();
    if (activeRings.length === 0 || this.zones.length === 0) {
      this.overlapZones = [];
      return;
    }

    this.overlapZones = this.zones.filter(zone => {
      if (zone.zone_uuid === this.editingZoneUuid || !zone.boundary_geojson) {
        return false;
      }

      const zoneRings = this.geometryToRings(zone.boundary_geojson);
      return zoneRings.some(zoneRing => activeRings.some(activeRing => this.ringsOverlap(activeRing, zoneRing)));
    });
  }

  private ringsOverlap(a: L.LatLng[], b: L.LatLng[]): boolean {
    if (a.length < 3 || b.length < 3 || !this.ringBoundsOverlap(a, b)) {
      return false;
    }

    if (this.ringSegmentsIntersect(a, b)) {
      return true;
    }

    return this.pointInRing(a[0], b) || this.pointInRing(b[0], a);
  }

  private ringBoundsOverlap(a: L.LatLng[], b: L.LatLng[]): boolean {
    const aBounds = this.ringBounds(a);
    const bBounds = this.ringBounds(b);
    return aBounds.minLat <= bBounds.maxLat
      && aBounds.maxLat >= bBounds.minLat
      && aBounds.minLng <= bBounds.maxLng
      && aBounds.maxLng >= bBounds.minLng;
  }

  private ringBounds(ring: L.LatLng[]): RingBounds {
    return ring.reduce((bounds, point) => ({
      minLat: Math.min(bounds.minLat, point.lat),
      maxLat: Math.max(bounds.maxLat, point.lat),
      minLng: Math.min(bounds.minLng, point.lng),
      maxLng: Math.max(bounds.maxLng, point.lng)
    }), {
      minLat: Infinity,
      maxLat: -Infinity,
      minLng: Infinity,
      maxLng: -Infinity
    });
  }

  private ringSegmentsIntersect(a: L.LatLng[], b: L.LatLng[]): boolean {
    for (let aIndex = 0; aIndex < a.length; aIndex++) {
      const aNext = (aIndex + 1) % a.length;
      for (let bIndex = 0; bIndex < b.length; bIndex++) {
        const bNext = (bIndex + 1) % b.length;
        if (this.segmentsIntersect(a[aIndex], a[aNext], b[bIndex], b[bNext])) {
          return true;
        }
      }
    }

    return false;
  }

  private segmentsIntersect(a1: L.LatLng, a2: L.LatLng, b1: L.LatLng, b2: L.LatLng): boolean {
    const o1 = this.orientation(a1, a2, b1);
    const o2 = this.orientation(a1, a2, b2);
    const o3 = this.orientation(b1, b2, a1);
    const o4 = this.orientation(b1, b2, a2);

    if (o1 !== o2 && o3 !== o4) {
      return true;
    }

    return (o1 === 0 && this.pointOnSegment(a1, b1, a2))
      || (o2 === 0 && this.pointOnSegment(a1, b2, a2))
      || (o3 === 0 && this.pointOnSegment(b1, a1, b2))
      || (o4 === 0 && this.pointOnSegment(b1, a2, b2));
  }

  private orientation(a: L.LatLng, b: L.LatLng, c: L.LatLng): number {
    const value = (b.lng - a.lng) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lng - a.lng);
    if (Math.abs(value) < 0.000000000001) {
      return 0;
    }

    return value > 0 ? 1 : 2;
  }

  private pointOnSegment(a: L.LatLng, point: L.LatLng, b: L.LatLng): boolean {
    const epsilon = 0.000000000001;
    const cross = (point.lat - a.lat) * (b.lng - a.lng) - (point.lng - a.lng) * (b.lat - a.lat);
    if (Math.abs(cross) > epsilon) {
      return false;
    }

    return point.lng <= Math.max(a.lng, b.lng) + epsilon
      && point.lng >= Math.min(a.lng, b.lng) - epsilon
      && point.lat <= Math.max(a.lat, b.lat) + epsilon
      && point.lat >= Math.min(a.lat, b.lat) - epsilon;
  }

  private pointInRing(point: L.LatLng, ring: L.LatLng[]): boolean {
    let inside = false;
    for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index++) {
      const currentPoint = ring[index];
      const previousPoint = ring[previous];
      if (this.pointOnSegment(previousPoint, point, currentPoint)) {
        return true;
      }

      const crossesRay = (currentPoint.lat > point.lat) !== (previousPoint.lat > point.lat);
      if (crossesRay) {
        const intersectionLng = ((previousPoint.lng - currentPoint.lng) * (point.lat - currentPoint.lat))
          / (previousPoint.lat - currentPoint.lat)
          + currentPoint.lng;
        if (point.lng < intersectionLng) {
          inside = !inside;
        }
      }
    }

    return inside;
  }

  private zoneDisplayName(zone: ZoneExpertZone): string {
    const label = zone.name || zone.zone_name || zone.zone_id || zone.zone_uuid || 'Zone';
    return String(label).trim() || 'Zone';
  }

  private geometryToRings(geometry: Geometry | null): L.LatLng[][] {
    if (!geometry) {
      return [];
    }

    if (geometry.type === 'Polygon') {
      return [this.positionsToRing(geometry.coordinates[0] || [])].filter(ring => ring.length >= 3);
    }

    if (geometry.type === 'MultiPolygon') {
      return geometry.coordinates
        .map(polygon => this.positionsToRing((polygon[0] || []) as Position[]))
        .filter(ring => ring.length >= 3);
    }

    return [];
  }

  private positionsToRing(positions: Position[]): L.LatLng[] {
    const ring = positions
      .map(position => this.positionToLatLng(position))
      .filter((point): point is L.LatLng => point !== null);

    if (ring.length > 1) {
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first.lat === last.lat && first.lng === last.lng) {
        ring.pop();
      }
    }

    return ring;
  }

  private positionToLatLng(position: Position): L.LatLng | null {
    const lng = Number(position[0]);
    const lat = Number(position[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    return L.latLng(lat, lng);
  }

  private requireMap(): L.Map {
    if (!this.map) {
      throw new Error('Map is not initialized');
    }

    return this.map;
  }

  private requireLayer(layer: L.LayerGroup | undefined): L.LayerGroup {
    if (!layer) {
      throw new Error('Map layer is not initialized');
    }

    return layer;
  }

  private apiErrorMessage(error: unknown, fallback: string): string {
    const apiError = error as { error?: { message?: string; errors?: string[] } | string; message?: string; status?: number };
    if (typeof apiError?.error === 'string' && apiError.error.trim() !== '') {
      return apiError.error;
    }

    if (typeof apiError?.error === 'object') {
      if (Array.isArray(apiError.error.errors) && apiError.error.errors.length > 0) {
        return apiError.error.errors[0];
      }
      if (apiError.error.message) {
        return apiError.error.message;
      }
    }

    if (apiError?.message) {
      return apiError.message;
    }

    if (apiError?.status) {
      return fallback + ' (' + apiError.status + ')';
    }

    return fallback;
  }
}
